import { SOURCE_KIND_WORDS, type SourceKind } from "./freshness";

/**
 * The analytics widget registry (ANL-E04-S09, FR-ADM-16, FR-ADM-24): every tile the first release
 * renders, with the source kind its freshness chip, as-of footer and source popover read, and the
 * access tier its region needs. Pure TypeScript with no React import.
 */

/** A aggregates; E explorer and I identity are specified, not provisioned (Step 9, gated). */
export type WidgetTier = "A" | "E" | "I";

export type WidgetDefinition = {
  id: string;
  // The tile's name in its Loading and Error states.
  label: string;
  tier: WidgetTier;
  sourceKind: SourceKind;
};

const productRecords = (slug: string, label: string, sourceKind: SourceKind): WidgetDefinition => ({
  id: `product-records.${slug}`,
  label,
  tier: "A",
  sourceKind,
});

// Product records reads product tables (O) and its mobile routes read event rows (E); the agentic
// checkout funnel reads the purchase-intent ledger (L). Kinds match each handler's `meta.source`.
export const FIRST_RELEASE_WIDGETS = [
  productRecords("overview", "Overview", "O"),
  productRecords("discover", "Discovery", "O"),
  productRecords("events", "Events", "O"),
  productRecords("wishlist", "Wishlist", "O"),
  productRecords("event-products", "Event products", "O"),
  productRecords("friends", "Friends", "O"),
  productRecords("badges", "Badges", "O"),
  productRecords("gift-splits", "Gift splits", "O"),
  productRecords("mobile-overview", "Mobile overview", "E"),
  productRecords("mobile-retention", "Retention (D1 / D7 / D30)", "E"),
  productRecords("mobile-screens", "Top screens by time", "E"),
  productRecords("mobile-events", "Top events", "E"),
  productRecords("mobile-referrals", "Referral funnel", "E"),
  productRecords("mobile-event-types", "Event type usage", "E"),
  { id: "agentic-checkout.funnel", label: "Agentic checkout funnel", tier: "A", sourceKind: "L" },
] as const satisfies readonly WidgetDefinition[];

export type WidgetId = (typeof FIRST_RELEASE_WIDGETS)[number]["id"];

const WIDGETS_BY_ID: ReadonlyMap<string, WidgetDefinition> = new Map(
  FIRST_RELEASE_WIDGETS.map((widget) => [widget.id, widget]),
);

export const widgetById = (id: WidgetId): WidgetDefinition => WIDGETS_BY_ID.get(id) as WidgetDefinition;

const SOURCE_KINDS = Object.keys(SOURCE_KIND_WORDS);

const isSourceKind = (value: unknown): value is SourceKind =>
  typeof value === "string" && SOURCE_KINDS.includes(value);

/** Every problem with a registry, each naming its tile; empty when the registry is valid (AC2). */
export const validateWidgetRegistry = (widgets: readonly unknown[]): string[] => {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const [index, widget] of widgets.entries()) {
    const record = (typeof widget === "object" && widget !== null ? widget : {}) as Record<string, unknown>;
    const id = typeof record.id === "string" && record.id.length > 0 ? record.id : `widget ${index}`;
    if (seen.has(id)) problems.push(`${id}: the id is used twice`);
    seen.add(id);
    if (!isSourceKind(record.sourceKind)) {
      problems.push(`${id}: sourceKind is required (one of ${SOURCE_KINDS.join(", ")})`);
    }
  }
  return problems;
};

export type TierRegion = "absent" | "placeholder" | "shown";

export type TierAccess = {
  // Tiers whose routes this build registers; the first release registers A only.
  registeredTiers: readonly WidgetTier[];
  viewerTiers: readonly WidgetTier[];
};

/**
 * Whether a region renders (AC7): absent while its tier has no routes (STATE-26), the 403
 * placeholder once they exist and the viewer lacks the tier, shown otherwise.
 */
export const selectTierRegion = (tier: WidgetTier, { registeredTiers, viewerTiers }: TierAccess): TierRegion => {
  if (!registeredTiers.includes(tier)) return "absent";
  return viewerTiers.includes(tier) ? "shown" : "placeholder";
};

const TIER_NAMES: Readonly<Record<WidgetTier, string>> = {
  A: "Aggregates",
  E: "Explorer",
  I: "Identity",
};

export type TierPlaceholder = { title: string; viewerTier: string; neededTier: string };

/** The 403 placeholder's copy: "Needs identity tier", with the viewer's tier and the needed one. */
export const tierPlaceholder = (viewer: WidgetTier, needed: WidgetTier): TierPlaceholder => ({
  title: `Needs ${TIER_NAMES[needed].toLowerCase()} tier`,
  viewerTier: TIER_NAMES[viewer],
  neededTier: TIER_NAMES[needed],
});
